import { SettingsSection } from "@/shared/components/visualization/PanelSettings/SettingsSection";
import { GridFields } from "@/shared/components/visualization/PanelSettings/ThreeDPanelSettings/SceneConfig/GridFields";
import { ThirdPersonCameraFields } from "@/shared/components/visualization/PanelSettings/ThreeDPanelSettings/SceneConfig/ThirdPersonCameraFields";
import type {
  Grid,
  ThirdPersonCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

import styles from "./PreviewSceneSettings.module.css";

interface PreviewSceneSettingsProps {
  camera: ThirdPersonCamera;
  grid: Grid;
  onCameraChange: (camera: ThirdPersonCamera) => void;
  onGridChange: (grid: Grid) => void;
}

/**
 * The preview's scenery and viewpoint controls, atop the preview's sidebar.
 *
 * A block within that sidebar rather than a column of its own: it carries no
 * width, no scroller and no rule against the viewport, because the sidebar
 * around it owns all three. Giving it its own `overflow-y` would make it a
 * scroll region nested inside an already-scrolling column.
 *
 * The same editors the 3D panel's settings sidebar uses, which is the point:
 * they were extracted from it rather than rebuilt here, so a grid edited in a
 * workspace and a grid edited in this wizard cannot drift into two different
 * ideas of what a grid is. What differs is only where the value lives — the
 * panel dispatches into the visualization store against a `panelId`, and this
 * preview has no panel in that store at all, its scene being plain React state
 * a modal owns. That is what made the editors presentational.
 *
 * The camera is **third person**, and its fields are the ones a third-person
 * camera actually has: a focus point, two angles and a distance. It is not the
 * orbit camera the panel's own camera editor speaks, which frames a tracked
 * entity within a zoom envelope — there is nothing to track in a static pose,
 * and no envelope in the spec.
 */
export function PreviewSceneSettings({
  camera,
  grid,
  onCameraChange,
  onGridChange,
}: PreviewSceneSettingsProps) {
  return (
    <section className={styles.sceneSettings}>
      <h2 className={styles.sceneTitle}>Scene</h2>
      {/* Both start closed. Two open forms above the joint list push the joints
          — what the user opened the preview to look at — below the fold, and
          adjusting the viewpoint is the rarer errand. `defaultExpanded` is an
          initial state, not a controlled one: whichever the user opens stays
          open for as long as the dialog does, through joint edits and any other
          re-render of this step. */}
      <SettingsSection title="Camera" collapsible defaultExpanded={false}>
        <ThirdPersonCameraFields value={camera} onChange={onCameraChange} />
      </SettingsSection>
      <SettingsSection title="Scenery" collapsible defaultExpanded={false}>
        <GridFields value={grid} onChange={onGridChange} />
      </SettingsSection>
    </section>
  );
}
